import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FilterBar } from "../components/insurance/filter-bar";
import { type InsuranceRow, InsuranceTable } from "../components/insurance/insurance-table";
import { Pagination } from "../components/insurance/pagination";
import { Card, CardContent } from "../components/ui/card";
import { PageLoading } from "../components/ui/loading";
import { useInsuranceList, useValuers } from "../hooks/use-api";

type SortField = "id" | "address" | "insured" | "valuer" | "pocTo" | "status" | "updatedAt";
type SortDirection = "asc" | "desc";

function toCsvField(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function exportRecordsToCsv(records: InsuranceRow[]): void {
  const headers = ["Reference", "Address", "Insured", "Valuer", "Status", "Updated"];
  const rows = records.map((record) => [
    record.id,
    record.address,
    record.insured,
    record.valuer,
    record.status,
    format(new Date(record.updatedAt), "yyyy-MM-dd"),
  ]);
  const csv = [headers, ...rows].map((row) => row.map(toCsvField).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `insurance-records-${format(new Date(), "yyyy-MM-dd")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function InsuranceListPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") ?? "";

  const [search, setSearch] = useState(initialSearch);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [valuer, setValuer] = useState("");
  const [category, setCategory] = useState("");
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [isExpiringActive, setIsExpiringActive] = useState(false);
  const [sortField, setSortField] = useState<SortField>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const params = useMemo(() => {
    const p: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
      sort: sortField,
      dir: sortDirection,
    };
    if (search) p.q = search;
    if (status) p.status = status;
    if (valuer) p.valuer = valuer;
    if (category) p.category = category;
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;
    if (includeSuperseded) p.includeSuperseded = "true";
    return p;
  }, [search, status, valuer, category, dateFrom, dateTo, includeSuperseded, sortField, sortDirection, page, pageSize]);

  const { data, isLoading } = useInsuranceList(params);
  const { data: valuersData } = useValuers();

  const records: InsuranceRow[] = (data?.records ?? []) as InsuranceRow[];
  const total = data?.total ?? 0;
  const valuerNames = (valuersData?.items ?? []).map((v) => v.name);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      setPage(1);
    },
    [sortField],
  );

  const handleApply = useCallback(() => setPage(1), []);
  const handleExport = useCallback(() => exportRecordsToCsv(records), [records]);

  const handleExpiringFilter = useCallback(() => {
    if (isExpiringActive) {
      // Clear expiring filter
      setDateFrom("");
      setDateTo("");
      setStatus("");
      setSortField("updatedAt");
      setSortDirection("desc");
      setIsExpiringActive(false);
    } else {
      // Sort by expiry ascending to surface expiring records
      setDateFrom("");
      setDateTo("");
      setStatus("active");
      setSortField("pocTo");
      setSortDirection("asc");
      setIsExpiringActive(true);
    }
    setPage(1);
  }, [isExpiringActive]);

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Insurance Records</h1>
        <p className="text-sm text-muted-foreground">
          {total.toLocaleString()} records{search ? ` matching "${search}"` : ""}
          {isExpiringActive ? " — sorted by expiry date" : ""}
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            dateFrom={dateFrom}
            onDateFromChange={setDateFrom}
            dateTo={dateTo}
            onDateToChange={setDateTo}
            status={status}
            onStatusChange={setStatus}
            valuer={valuer}
            onValuerChange={setValuer}
            category={category}
            onCategoryChange={setCategory}
            includeSuperseded={includeSuperseded}
            onIncludeSupersededChange={setIncludeSuperseded}
            onApply={handleApply}
            onExport={handleExport}
            onExpiringFilter={handleExpiringFilter}
            valuers={valuerNames}
            isExpiringActive={isExpiringActive}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <PageLoading />
          ) : (
            <>
              <InsuranceTable
                records={records}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
